import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { chatService } from "../services/chatService";
import { useEchoChannel } from "./useEchoChannel";
import { useChatNotifications } from "./useChatNotifications";
import { useTypingIndicator } from "./useTypingIndicator";

const PUBLIC = 'public';
const PRIVATE = 'private';

/** Añade un mensaje descartando el eco del que ya está pintado. */
const append = (list, message) => (
    list.some((item) => item.id === message.id) ? list : [...list, message]
);

// Contador propio: dos avisos en el mismo milisegundo compartirían key.
let noticeSeq = 0;

const systemNotice = (text) => ({
    id: `local-${Date.now()}-${noticeSeq++}`,
    message: text,
    kind: 'system',
    type: null,
    user_id: null,
    user_name: 'Sistema',
    timestamp: new Date().toISOString()
});

/**
 * Estado completo del chat de una sala.
 *
 * La sala tiene dos superficies a la vez y hay que sostener las dos aunque
 * solo se vea una:
 *
 *  - el canal público, que además es de presencia y por tanto la única fuente
 *    honesta del número de espectadores;
 *  - los hilos privados. Un cliente tiene exactamente uno, con la modelo; la
 *    modelo tiene uno por cada cliente que le escribe y va cambiando entre
 *    ellos sin perder los mensajes que llegan a los demás, que es lo que le
 *    permite atender a varias personas a la vez.
 *
 * Por eso el hilo abierto y la pestaña visible son cosas distintas: se sigue
 * escuchando el hilo privado mientras se mira el chat público, y lo que llega
 * fuera de la vista se anota en la campanita en lugar de perderse.
 */
export const useRoomChat = (room, user) => {
    const roomId = room?.id ?? null;
    const userId = user?.id ?? null;
    const isModel = Boolean(room && userId && room.user_id === userId);

    const [tab, setTab] = useState(PUBLIC);
    const [publicMessages, setPublicMessages] = useState([]);
    const [threadMessages, setThreadMessages] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [viewers, setViewers] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // La modelo elige con quién habla; el cliente solo tiene su propio hilo.
    const [selectedThread, setSelectedThread] = useState(null);
    const activeThread = isModel ? selectedThread : userId;

    const notifications = useChatNotifications();
    const publicTyping = useTypingIndicator();
    const threadTyping = useTypingIndicator();

    // Se extraen las funciones estables de cada hook: los objetos que los
    // envuelven cambian de identidad en cada render y arrastrarían con ellos a
    // todos los callbacks que dependieran de ellos.
    const { push: notify, markSeen } = notifications;
    const { reset: resetPublicTyping } = publicTyping;
    const { reset: resetThreadTyping } = threadTyping;

    // Lo que el usuario está mirando en este instante. Los handlers del socket
    // se registran una sola vez, así que leen de una ref en vez de capturar el
    // valor del render en el que se suscribieron.
    const viewRef = useRef({ tab, activeThread });
    viewRef.current = { tab, activeThread };

    const isVisible = useCallback((surface, threadId = null) => {
        if (typeof document !== 'undefined' && document.hidden) return false;
        if (surface === PUBLIC) return viewRef.current.tab === PUBLIC;
        return viewRef.current.tab === PRIVATE && viewRef.current.activeThread === threadId;
    }, []);

    /* ------------------------------------------------------------------
       Canal público de la sala. Es de presencia: el mismo socket transporta
       el chat abierto y la lista de quién está viendo la transmisión.

       Los tres canales usan prefijos distintos en vez de anidarse bajo el de
       la sala; el porqué está en routes/channels.php del backend, y los
       nombres tienen que coincidir carácter a carácter con los de allí.
       ------------------------------------------------------------------ */
    const room_channel = roomId ? `chat-room.${roomId}` : null;

    const { connected, status, whisper: whisperPublic } = useEchoChannel(room_channel, {
        visibility: 'presence',
        events: {
            MessageSent: ({ message }) => {
                if (!message) return;
                setPublicMessages((prev) => append(prev, message));
                publicTyping.forget(message.user_id);

                if (message.user_id === userId || isVisible(PUBLIC)) return;

                notify({
                    surface: PUBLIC,
                    title: message.user_name,
                    body: message.message,
                    tip: message.kind === 'tip'
                });
            }
        },
        presence: {
            here: (members) => setViewers(members),
            joining: (member) => setViewers((prev) => (
                prev.some((viewer) => viewer.id === member?.id) ? prev : [...prev, member]
            )),
            leaving: (member) => setViewers((prev) => prev.filter((viewer) => viewer.id !== member?.id))
        },
        whispers: {
            typing: (payload) => {
                if (payload?.id === userId) return;
                publicTyping.receive(payload);
            }
        }
    });

    /* ------------------------------------------------------------------
       Hilo privado abierto.
       ------------------------------------------------------------------ */
    const thread_channel = roomId && activeThread
        ? `chat-thread.${roomId}.${activeThread}`
        : null;

    const { whisper: whisperThread } = useEchoChannel(thread_channel, {
        visibility: 'private',
        events: {
            MessageSent: ({ message }) => {
                if (!message) return;
                setThreadMessages((prev) => append(prev, message));
                threadTyping.forget(message.user_id);

                if (message.user_id === userId) return;

                if (!isVisible(PRIVATE, message.thread_id)) {
                    notify({
                        surface: PRIVATE,
                        thread: message.thread_id,
                        title: message.user_name,
                        body: message.message
                    });
                } else {
                    // Está a la vista: se marca leído para que el contador de
                    // la modelo no acumule mensajes que ya está leyendo.
                    chatService.markAsRead(roomId, message.thread_id).catch(() => {});
                }
            }
        },
        whispers: {
            typing: (payload) => {
                if (payload?.id === userId) return;
                threadTyping.receive(payload);
            }
        }
    });

    /* ------------------------------------------------------------------
       Bandeja de la modelo: hilos que todavía no tiene abiertos.
       ------------------------------------------------------------------ */
    const inbox_channel = isModel && roomId ? `chat-inbox.${roomId}` : null;

    useEchoChannel(inbox_channel, {
        visibility: 'private',
        events: {
            ThreadUpdated: ({ message }) => {
                if (!message?.thread_id) return;

                const open = isVisible(PRIVATE, message.thread_id);

                setConversations((prev) => {
                    const rest = prev.filter((item) => item.client_id !== message.thread_id);
                    const current = prev.find((item) => item.client_id === message.thread_id);
                    const mine = message.user_id === userId;

                    return [{
                        client_id: message.thread_id,
                        client_name: mine ? (current?.client_name ?? 'Usuario') : message.user_name,
                        last_message: message.message,
                        last_at: message.timestamp,
                        unread: mine || open ? 0 : (current?.unread ?? 0) + 1
                    }, ...rest];
                });

                if (!open && message.user_id !== userId) {
                    notify({
                        surface: PRIVATE,
                        thread: message.thread_id,
                        title: message.user_name,
                        body: message.message
                    });
                }
            }
        }
    });

    /* ------------------------------------------------------------------
       Historial
       ------------------------------------------------------------------ */
    useEffect(() => {
        if (!roomId) return;

        let cancelled = false;

        chatService.getHistory(roomId, PUBLIC)
            .then((data) => {
                if (cancelled) return;
                // El historial manda sobre lo recibido en vivo mientras cargaba:
                // se conserva lo que llegó por socket y no viene en la página.
                setPublicMessages((live) => {
                    const known = new Set((data?.messages || []).map((item) => item.id));
                    return [
                        ...(data?.messages || []),
                        ...live.filter((item) => !known.has(item.id))
                    ];
                });
            })
            .catch(() => {
                if (!cancelled) {
                    setPublicMessages((prev) => [...prev, systemNotice('No se pudo cargar el chat de la sala.')]);
                }
            });

        return () => { cancelled = true; };
    }, [roomId]);

    useEffect(() => {
        if (!roomId || !activeThread) {
            setThreadMessages([]);
            return undefined;
        }

        let cancelled = false;
        setLoadingHistory(true);

        chatService.getHistory(roomId, PRIVATE, activeThread)
            .then((data) => {
                if (!cancelled) setThreadMessages(data?.messages || []);
            })
            .catch(() => {
                if (!cancelled) setThreadMessages([systemNotice('No se pudo cargar esta conversación.')]);
            })
            .finally(() => {
                if (!cancelled) setLoadingHistory(false);
            });

        return () => { cancelled = true; };
    }, [roomId, activeThread]);

    const loadConversations = useCallback(() => {
        if (!isModel || !roomId) return;
        chatService.getConversations(roomId)
            .then((data) => setConversations(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, [isModel, roomId]);

    useEffect(() => { loadConversations(); }, [loadConversations]);

    /* ------------------------------------------------------------------
       Acciones
       ------------------------------------------------------------------ */
    const openThread = useCallback((clientId) => {
        setTab(PRIVATE);
        if (isModel) setSelectedThread(clientId);

        setConversations((prev) => prev.map((item) => (
            item.client_id === clientId ? { ...item, unread: 0 } : item
        )));

        markSeen((item) => item.surface === PRIVATE && item.thread === clientId);

        if (roomId && clientId) {
            chatService.markAsRead(roomId, clientId).catch(() => {});
        }
    }, [isModel, roomId, markSeen]);

    const showTab = useCallback((next) => {
        setTab(next);
        markSeen((item) => item.surface === next);
    }, [markSeen]);

    const send = useCallback(async (text) => {
        const body = text.trim();
        if (!body || !roomId) return false;

        if (tab === PRIVATE && !activeThread) {
            setThreadMessages((prev) => [...prev, systemNotice('Elige primero con quién quieres hablar.')]);
            return false;
        }

        try {
            const message = await chatService.sendMessage({
                message: body,
                room_id: roomId,
                type: tab,
                thread: tab === PRIVATE && isModel ? activeThread : null
            });

            // El backend responde con el mensaje ya guardado; pintarlo aquí
            // evita el hueco entre pulsar enviar y recibir el broadcast.
            if (message?.id) {
                const target = tab === PRIVATE ? setThreadMessages : setPublicMessages;
                target((prev) => append(prev, message));
            }

            (tab === PRIVATE ? resetThreadTyping : resetPublicTyping)();
            return true;
        } catch (error) {
            const notice = systemNotice(`No se pudo enviar tu mensaje: ${error.message}`);
            const target = tab === PRIVATE ? setThreadMessages : setPublicMessages;
            target((prev) => [...prev, notice]);
            return false;
        }
    }, [roomId, tab, activeThread, isModel, resetThreadTyping, resetPublicTyping]);

    /** Avisa a la otra parte de que se está escribiendo, como mucho cada 2 s. */
    const signalTyping = useCallback(() => {
        if (!user) return;

        const shouldSignal = tab === PRIVATE ? threadTyping.shouldSignal : publicTyping.shouldSignal;
        if (!shouldSignal()) return;

        const whisper = tab === PRIVATE ? whisperThread : whisperPublic;
        whisper('typing', { id: user.id, name: user.name });
    }, [tab, user, threadTyping.shouldSignal, publicTyping.shouldSignal, whisperThread, whisperPublic]);

    const messages = tab === PRIVATE ? threadMessages : publicMessages;
    const typing = tab === PRIVATE ? threadTyping.typing : publicTyping.typing;

    // La modelo se cuenta a sí misma cuando entra a su sala: el aforo es el
    // público, no el reparto.
    const viewerCount = useMemo(
        () => viewers.filter((viewer) => !viewer?.is_model).length,
        [viewers]
    );

    return {
        isModel,
        tab,
        showTab,
        messages,
        typing,
        viewers,
        viewerCount,
        conversations,
        activeThread,
        openThread,
        loadingHistory,
        send,
        signalTyping,
        notifications,
        status,
        connected
    };
};
